/**
 * Shared Excel export styling utility
 * Light, professional, office-ready register style
 */
import XLSX from "xlsx-js-style";
import JSZip from "jszip";

// Light professional color palette
const COLORS = {
  titleText: "1f3864",      // Dark blue text for register name
  subtitleText: "595959",    // Grey for "Gruppo Impresa STP"
  periodText: "833c0b",     // Dark brown text for period
  headerBg: "4472c4",       // Medium blue for column headers
  headerText: "ffffff",     // White text on header
  evenRowBg: "f2f7fb",      // Very light blue for alternating rows
  borderColor: "b4c6e7",    // Soft blue border
  dataText: "1f1f1f",       // Dark text for readability
};

// Thin border style
const thinBorder = {
  top: { style: "thin", color: { rgb: COLORS.borderColor } },
  bottom: { style: "thin", color: { rgb: COLORS.borderColor } },
  left: { style: "thin", color: { rgb: COLORS.borderColor } },
  right: { style: "thin", color: { rgb: COLORS.borderColor } },
};

/**
 * Apply professional light styling to a worksheet
 */
export function applyRegisterStyle(ws, numCols, dataStartRow, totalRows, moneyColumns = [], centerColumns = []) {
  // Row 0: Register name (main title) — white bg, bold, large
  for (let c = 0; c < numCols; c++) {
    const ref = XLSX.utils.encode_cell({ r: 0, c });
    if (!ws[ref]) ws[ref] = { v: "", t: "s" };
    ws[ref].s = {
      font: { bold: true, sz: 14, color: { rgb: COLORS.titleText } },
      alignment: { horizontal: "left", vertical: "center" },
      fill: { fgColor: { rgb: "ffffff" } },
    };
  }

  // Row 1: "Gruppo Impresa STP" (secondary) — white bg, smaller
  for (let c = 0; c < numCols; c++) {
    const ref = XLSX.utils.encode_cell({ r: 1, c });
    if (!ws[ref]) ws[ref] = { v: "", t: "s" };
    ws[ref].s = {
      font: { sz: 10, color: { rgb: COLORS.subtitleText } },
      alignment: { horizontal: "left", vertical: "center" },
      fill: { fgColor: { rgb: "ffffff" } },
    };
  }

  // Row 2: Period / Gestione / Export date — white bg
  for (let c = 0; c < numCols; c++) {
    const ref = XLSX.utils.encode_cell({ r: 2, c });
    if (!ws[ref]) ws[ref] = { v: "", t: "s" };
    ws[ref].s = {
      font: { sz: 10, color: { rgb: COLORS.periodText } },
      alignment: { horizontal: "left", vertical: "center" },
      fill: { fgColor: { rgb: "ffffff" } },
    };
  }

  // Data header row — keep current style
  for (let c = 0; c < numCols; c++) {
    const ref = XLSX.utils.encode_cell({ r: dataStartRow, c });
    if (!ws[ref]) ws[ref] = { v: "", t: "s" };
    const isMoney = moneyColumns.includes(c);
    ws[ref].s = {
      font: { bold: true, sz: 10, color: { rgb: COLORS.headerText } },
      fill: { fgColor: { rgb: COLORS.headerBg } },
      alignment: { horizontal: isMoney ? "right" : "center", vertical: "center", wrapText: true },
      border: thinBorder,
    };
  }

  // Data rows — alternating: 1st=light blue, 2nd=white...
  for (let i = dataStartRow + 1; i < totalRows; i++) {
    const rowIndex = i - dataStartRow - 1;
    const isBlue = rowIndex % 2 === 0;
    for (let c = 0; c < numCols; c++) {
      const ref = XLSX.utils.encode_cell({ r: i, c });
      if (!ws[ref]) ws[ref] = { v: "", t: "s" };
      const isMoney = moneyColumns.includes(c);
      const isCenter = centerColumns.includes(c);
      ws[ref].s = {
        font: { sz: 10, color: { rgb: COLORS.dataText } },
        alignment: { horizontal: isMoney ? "right" : (isCenter ? "center" : "left"), vertical: "center" },
        fill: { fgColor: { rgb: isBlue ? COLORS.evenRowBg : "ffffff" } },
        border: thinBorder,
      };
      if (isMoney && typeof ws[ref].v === "number") {
        ws[ref].z = "#,##0.00";
      }
    }
  }

  // Row heights
  ws["!rows"] = [];
  ws["!rows"][0] = { hpt: 28 };
  ws["!rows"][1] = { hpt: 18 };
  ws["!rows"][2] = { hpt: 18 };
  ws["!rows"][dataStartRow] = { hpt: 22 };

  // AutoFilter on data header row
  ws["!autofilter"] = {
    ref: XLSX.utils.encode_range({
      s: { r: dataStartRow, c: 0 },
      e: { r: totalRows - 1, c: numCols - 1 },
    }),
  };
}

/**
 * Write workbook to file with REAL landscape orientation and print scaling.
 * xlsx-js-style does NOT write pageSetup to the XML, so we post-process the ZIP.
 * @param {Object} wb - workbook
 * @param {string} fileName - output file name
 * @param {number} scale - print zoom % (e.g. 72, 84, 88)
 */
export async function writeFileWithPageSetup(wb, fileName, scale) {
  const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });

  const zip = await JSZip.loadAsync(buf);
  const sheetFiles = Object.keys(zip.files).filter(f => f.startsWith("xl/worksheets/sheet"));

  const pageTags =
    `<pageMargins left="0.4" right="0.4" top="0.5" bottom="0.5" header="0.3" footer="0.3"/>` +
    `<pageSetup orientation="landscape" paperSize="9" scale="${scale}"/>`;

  for (const sheetPath of sheetFiles) {
    let xml = await zip.file(sheetPath).async("string");
    if (!xml.includes("pageSetup")) {
      // OOXML element order: ...mergeCells, pageMargins, pageSetup, ignoredErrors...
      // Insert BEFORE ignoredErrors if present, otherwise before </worksheet>
      if (xml.includes("<ignoredErrors")) {
        xml = xml.replace("<ignoredErrors", pageTags + "<ignoredErrors");
      } else {
        xml = xml.replace("</worksheet>", pageTags + "</worksheet>");
      }
      zip.file(sheetPath, xml);
    }
  }

  const blob = await zip.generateAsync({
    type: "blob",
    mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    compression: "DEFLATE",
    compressionOptions: { level: 6 },
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/**
 * Format date for filename: YYYY-MM-DD
 */
export function fileDate() {
  return new Date().toISOString().split("T")[0];
}
